import { ArrowLeft } from "react-feather"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { tenantApi } from "@/api/client/tenant"
import type { TenantCreateBodyPurpose } from "@/api/generated/models/tenantCreateBodyPurpose"
import { getErrorMessage } from "@/api/instance"
import { TenantForm, type TenantFormValues } from "@/components/room"
import { toOccupantsPayload } from "@/components/room/tenant-form/TenantForm"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"

export function TenantAddPage() {
  const navigate = useNavigate()

  // tenantCreateのmutationを取得
  const { mutateAsync: createTenant, isPending } = tenantApi.useTenantCreate({
    mutation: {
      onError: (error) =>
        toast.error("賃借人の追加に失敗しました", { description: getErrorMessage(error) }),
    },
  })

  const handleSubmit = async (values: TenantFormValues) => {
    // roomIdがない場合はエラー
    if (!values.roomId) {
      toast.error("部屋を選択してください")
      return
    }

    try {
      const requestData = {
        roomId: Number(values.roomId),
        name: values.name,
        nameKana: values.nameKana,
        phone1: values.phone1,
        phone2: values.phone2,
        phone3: values.phone3,
        phone4: values.phone4,
        phone5: values.phone5,
        phone6: values.phone6,
        email: values.email,
        birthday: values.birthday && values.birthday.length > 0 ? values.birthday : null,
        startDate: values.startDate,
        rent: Number(values.rent || 0),
        commonAreaFee: Number(values.commonAreaFee || 0),
        isParking: values.isParking,
        parkingFee: Number(values.parkingFee || 0),
        parkingNumber: values.parkingNumber || "",
        otherFees: values.otherFees.map((fee) => ({
          label: fee.name,
          price: Number(fee.baseAmount),
          tax: Number(fee.tax),
        })),
        purpose: values.purpose as TenantCreateBodyPurpose,
        occupants: toOccupantsPayload(values.occupants),
      }

      await createTenant({ data: requestData })

      toast.success("賃借人を追加しました")
      navigate("/tenants", { state: { refetch: true } })
    } catch {
      // 失敗の表示は mutation の onError が担当する
    }
  }

  const handleCancel = () => {
    navigate("/tenants")
  }

  return (
    <ReactErrorBoundary>
      <div>
        <div className="flex items-center gap-2 p-6">
          <button
            type="button"
            onClick={() => navigate("/tenants")}
            className="flex items-center justify-center w-7 h-7 bg-white rounded-[5px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]"
          >
            <ArrowLeft size={14} className="text-[#354267]" />
          </button>
          <span className="text-sm font-semibold text-[#3f4652]">賃借人一覧に戻る</span>
        </div>

        <div className="max-w-[895px] mx-auto px-6 pb-8">
          <TenantForm
            defaultValues={{
              purpose: "1",
              name: "",
              nameKana: "",
              phone1: "",
              phone2: "",
              phone3: "",
              phone4: "",
              phone5: "",
              phone6: "",
              email: "",
              birthday: "",
              startDate: "",
              rent: "",
              commonAreaFee: "",
              isParking: false,
              parkingNumber: "",
              parkingFee: "",
              otherFees: [],
              occupants: [],
            }}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isPending}
            submitLabel="登録する"
          />
        </div>
      </div>
    </ReactErrorBoundary>
  )
}
